import { Component, OnInit, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuthService, User } from '../services/auth.service';
import { ToastService } from '../services/toast.service';
import { I18nService } from '../services/i18n.service';

@Component({
  selector: 'app-user-profile-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-100 p-4">
      <div class="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col">
        <!-- Header -->
        <div class="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <h2 class="text-xl font-bold text-gray-900 flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-primary"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            {{ i18n.t('profile.title') }}
          </h2>
          <button (click)="onClose.emit()" class="btn-modal-close">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" x2="6" y1="6" y2="18"/><line x1="6" x2="18" y1="6" y2="18"/></svg>
          </button>
        </div>

        <!-- Body -->
        <div class="p-8 overflow-y-auto space-y-8 flex-1">
          <!-- User Info Summary -->
          <div class="flex items-center gap-5 p-4 bg-slate-50 rounded-2xl border border-gray-100">
             <div class="w-16 h-16 bg-slate-800 text-white rounded-full flex items-center justify-center text-2xl font-bold shadow-lg shadow-slate-200">
                {{ (currentUser?.username || 'U').charAt(0).toUpperCase() }}
             </div>
             <div>
                <h3 class="text-lg font-bold text-gray-900">{{ currentUser?.username }}</h3>
                <p class="text-sm text-gray-500">{{ currentUser?.email }}</p>
             </div>
          </div>

          <!-- Language Switcher -->
          <div class="space-y-3">
            <h4 class="text-sm font-semibold text-gray-700 uppercase tracking-wider">{{ i18n.t('profile.language') }}</h4>
            <div class="flex gap-2">
              <button 
                (click)="i18n.setLanguage('it')"
                [class.ring-2]="i18n.lang() === 'it'"
                class="flex-1 flex items-center justify-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200 hover:bg-white transition-all ring-slate-800 ring-offset-2"
              >
                <span class="text-xl">🇮🇹</span>
                <span class="text-sm font-medium">Italiano</span>
              </button>
              <button 
                (click)="i18n.setLanguage('en')"
                [class.ring-2]="i18n.lang() === 'en'"
                class="flex-1 flex items-center justify-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200 hover:bg-white transition-all ring-slate-800 ring-offset-2"
              >
                <span class="text-xl">🇬🇧</span>
                <span class="text-sm font-medium">English</span>
              </button>
            </div>
          </div>

          <!-- Change Password Form -->
          <form (ngSubmit)="onChangePassword()" class="space-y-4">
            <h4 class="text-sm font-semibold text-gray-700 uppercase tracking-wider">{{ i18n.t('profile.passwordTitle') }}</h4>
            
            <div class="space-y-4">
              <div class="space-y-1.5">
                <label class="block text-xs font-medium text-gray-500">{{ i18n.t('profile.oldPassword') }}</label>
                <input
                  type="password"
                  [(ngModel)]="oldPassword"
                  name="oldPassword"
                  class="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:bg-white outline-none transition-all placeholder:text-gray-400"
                  required
                />
              </div>
              <div class="space-y-1.5">
                <label class="block text-xs font-medium text-gray-500">{{ i18n.t('profile.newPassword') }}</label>
                <input
                  type="password"
                  [(ngModel)]="newPassword"
                  name="newPassword"
                  class="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:bg-white outline-none transition-all placeholder:text-gray-400"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              [disabled]="changingPassword"
              class="w-full mt-4 btn-system-primary py-3"
            >
              {{ changingPassword ? i18n.t('profile.updating') : i18n.t('profile.updateButton') }}
            </button>
          </form>
        </div>

        <!-- Logout Footer -->
        <div class="p-6 border-t border-gray-100 flex items-center justify-between bg-gray-50/50">
           <span class="text-xs text-gray-400 font-mono">ID: {{ currentUser?.id }}</span>
           <button 
             (click)="onLogout()"
             class="btn-system-danger py-2! px-4! text-sm!"
           >
             <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" x2="9" y1="12" y2="12"/></svg>
             {{ i18n.t('dashboard.logout') }}
           </button>
        </div>
      </div>
    </div>
  `,
})
export class UserProfileModalComponent implements OnInit {
  @Output() onClose = new EventEmitter<void>();
  @Output() onLogoutRequested = new EventEmitter<void>();

  currentUser: User | null = null;
  oldPassword = '';
  newPassword = '';
  changingPassword = false;

  constructor(
    private authService: AuthService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
    public i18n: I18nService
  ) { }

  ngOnInit() {
    this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
    });
  }

  onChangePassword(): void {
    if (!this.oldPassword || !this.newPassword) {
      this.toastService.error(this.i18n.t('login.username') + ' & ' + this.i18n.t('login.password') + ' ' + this.i18n.t('common.required'));
      return;
    }

    if (this.newPassword.length < 6) {
      this.toastService.error(this.i18n.t('toast.passwordError'));
      return;
    }

    this.changingPassword = true;

    this.authService.changePassword(this.oldPassword, this.newPassword).subscribe({
      next: () => {
        this.toastService.success(this.i18n.t('toast.passwordSuccess'));
        this.oldPassword = '';
        this.newPassword = '';
        this.changingPassword = false;
        this.onClose.emit();
      },
      error: (err) => {
        const message = err.error?.error || this.i18n.t('toast.passwordError');
        this.toastService.error(message);
        this.changingPassword = false;
      }
    });
  }

  onLogout() {
    this.onLogoutRequested.emit();
    this.onClose.emit();
  }
}
